import { useState } from 'react';
import {
  Button, IconButton, Tooltip, Chip, Stack, MenuItem, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Typography, Alert, List, ListItem, ListItemText, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Build } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable, type Column } from '../../../components/DataTable';
import { StatusBadge } from '../../../components/StatusBadge';
import { serviceTicketsApi, type ServiceTicket } from '../../../api/serviceTickets.api';
import { FileUploadField } from '../../../components/FileUploadField';
import { pmExecutionApi } from '../../../api/evcpdAmc.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

function PmExecutionDialog({ ticketId, onClose }: { ticketId: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const open = ticketId !== null;
  const id = ticketId ?? 0;
  const [error, setError] = useState<string | null>(null);
  const [obs, setObs] = useState('');
  const [rec, setRec] = useState('');
  const [customer, setCustomer] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoKind, setPhotoKind] = useState('before');

  const { data: x } = useQuery({ queryKey: ['pm-exec', id], queryFn: () => pmExecutionApi.get(id), enabled: open });
  const refresh = () => { setError(null); qc.invalidateQueries({ queryKey: ['pm-exec', id] }); };
  const onErr = (e: unknown) => setError(extractApiError(e));

  const startMut = useMutation({ mutationFn: () => pmExecutionApi.start(id), onSuccess: refresh, onError: onErr });
  const respondMut = useMutation({ mutationFn: ({ rid, status }: { rid: number; status: string }) => pmExecutionApi.respond(id, rid, { status }), onSuccess: refresh, onError: onErr });
  const photoMut = useMutation({ mutationFn: () => pmExecutionApi.photo(id, { kind: photoKind, fileName: photoUrl.split('/').pop() || 'p.jpg', fileUrl: photoUrl }), onSuccess: () => { setPhotoUrl(''); refresh(); }, onError: onErr });
  const obsMut = useMutation({ mutationFn: () => pmExecutionApi.observations(id, obs), onSuccess: refresh, onError: onErr });
  const recMut = useMutation({ mutationFn: () => pmExecutionApi.recommendation(id, rec), onSuccess: refresh, onError: onErr });
  const signMut = useMutation({ mutationFn: () => pmExecutionApi.signature(id, { customerName: customer }), onSuccess: refresh, onError: onErr });
  const completeMut = useMutation({ mutationFn: () => pmExecutionApi.complete(id), onSuccess: () => { refresh(); onClose(); }, onError: onErr });

  if (!x) return null;
  const s = x.ticket.status;
  const mandatoryPending = x.checklist.filter((c) => c.mandatory && c.status === 'pending').length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <span>PM — {x.ticket.ticketCode}</span>
          <StatusBadge status={x.ticket.status} />
          {x.contract && <Chip size="small" label={x.contract.amcCode} variant="outlined" />}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {s === 'scheduled' || s === 'assigned' || s === 'open' ? (
          <Button fullWidth variant="contained" onClick={() => startMut.mutate()} disabled={startMut.isPending}>Start Visit</Button>
        ) : (
          <>
            <Divider textAlign="left" sx={{ mb: 1 }}>Checklist ({mandatoryPending} mandatory pending)</Divider>
            <List dense>
              {x.checklist.map((c) => (
                <ListItem key={c.id} divider secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    {['done', 'na', 'fail'].map((st) => (
                      <Button key={st} size="small" variant={c.status === st ? 'contained' : 'outlined'} color={st === 'fail' ? 'error' : st === 'done' ? 'success' : 'inherit'} onClick={() => respondMut.mutate({ rid: c.id, status: st })}>{st}</Button>
                    ))}
                  </Stack>
                }>
                  <ListItemText primary={`${c.title}${c.mandatory ? ' *' : ''}`} />
                </ListItem>
              ))}
            </List>

            <Divider textAlign="left" sx={{ my: 1 }}>Photos</Divider>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <TextField select size="small" label="Kind" value={photoKind} onChange={(e) => setPhotoKind(e.target.value)} sx={{ minWidth: 100 }}>
                {['site', 'before', 'after'].map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
              </TextField>
              <FileUploadField label="Photo" accept=".jpg,.jpeg,.png,.webp,.gif,.pdf" value={photoUrl || null} onChange={(url) => setPhotoUrl(url ?? '')} />
              <Button size="small" disabled={!photoUrl} onClick={() => photoMut.mutate()}>Add</Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
              {(x.ticket.photos ?? []).map((p) => <Chip key={p.id} size="small" label={`${p.kind}`} component="a" href={p.fileUrl} target="_blank" clickable />)}
            </Stack>

            <Divider textAlign="left" sx={{ my: 1 }}>Observations & Recommendation</Divider>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1}>
                <TextField size="small" label="Observations" value={obs || x.ticket.rootCause || ''} onChange={(e) => setObs(e.target.value)} fullWidth multiline />
                <Button size="small" disabled={!obs || s !== 'in_progress'} onClick={() => obsMut.mutate()}>Save</Button>
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField size="small" label="Recommendation" value={rec || x.ticket.resolutionRemarks || ''} onChange={(e) => setRec(e.target.value)} fullWidth multiline />
                <Button size="small" disabled={!rec || !['diagnosed', 'pending_spare', 'in_progress'].includes(s)} onClick={() => recMut.mutate()}>Save</Button>
              </Stack>
            </Stack>

            <Divider textAlign="left" sx={{ my: 1 }}>Customer Sign-off</Divider>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField size="small" label="Customer name" value={customer} onChange={(e) => setCustomer(e.target.value)} fullWidth />
              <Button size="small" disabled={!customer || s !== 'resolved'} onClick={() => signMut.mutate()}>{x.ticket.customerConfirmed ? 'Signed ✓' : 'Sign-off'}</Button>
            </Stack>
            {x.ticket.customerConfirmed && <Typography variant="caption" color="success.main">Customer confirmed.</Typography>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!['closed', 'cancelled'].includes(s) && (
          <Button variant="contained" color="success" disabled={mandatoryPending > 0 || !x.ticket.customerConfirmed || completeMut.isPending} onClick={() => completeMut.mutate()}>Complete PM</Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export function PmExecutionPage() {
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [execId, setExecId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['pm-tickets', page, limit],
    queryFn: () => serviceTicketsApi.list({ page, limit, ticketType: 'preventive_maintenance' }),
  });

  const columns: Column<ServiceTicket>[] = [
    { key: 'ticketCode', label: 'Ticket', width: 160 },
    { key: 'priority', label: 'Priority' },
    { key: 'scheduledDate', label: 'Scheduled', render: (r) => r.scheduledDate ?? '—' },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader title="PM Execution" subtitle="Technician preventive-maintenance visits: checklist, photos, sign-off, completion." />
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          hasPermission('evcpd_pm.execute') ? <Tooltip title="Execute PM"><IconButton size="small" onClick={() => setExecId(r.id)}><Build fontSize="small" /></IconButton></Tooltip> : null
        )}
      />
      <PmExecutionDialog ticketId={execId} onClose={() => setExecId(null)} />
    </>
  );
}
